/**
 * Pure builder for the add-column payload (ADR-0010 table designer).
 *
 * Extracted from `TableDesignerPage`'s `AddColumnDialog` so the per-kind
 * mapping (relation / relation_many / lookup / formula / select / plain) is
 * unit-testable without mounting the dialog. The UI owns the state; this owns
 * the kind → `ColumnIn` translation.
 */
import type { ColumnIn } from "./api-apps";

export interface ColumnDraft {
  name: string;
  /** The (possibly synthetic) type chosen in the dropdown. */
  dataType: string;
  required: boolean;
  /** `select`: the de-duplicated option list. */
  options: string[];
  /** `relation` / `relation_many`: the linked table id. */
  targetTable: string;
  /** `relation`: on-delete behaviour (restrict | set_null | cascade). */
  onDelete: string;
  /** `lookup`: pull <field> across the relation column <relation>. */
  lookupRelation: string;
  lookupField: string;
  /** `rollup`: aggregate child rows of <source> via <via>. */
  rollupSource: string;
  rollupVia: string;
  rollupOp: string;
  rollupField: string;
  /** `rollup`: optional AND-list of child-row filters {column, op, value?}. */
  rollupFilters: { column: string; op: string; value?: unknown }[];
  /** `formula`: a per-row JS expression over the row's own fields + its result
   *  type (the value it's coerced to). */
  formulaExpression: string;
  formulaResultType: string;
}

/** Computed column kinds — read-time only, never writable, so excluded from
 *  every edit surface (forms, grid edit dialog, portal record/form). */
export function isComputedColumn(c: { kind?: string | null }): boolean {
  return c.kind === "lookup" || c.kind === "rollup" || c.kind === "formula";
}

/** A human label for a column's TYPE that reflects its `kind`, not the raw
 *  physical `data_type` (a relation is stored as `uuid`, a relation_many as
 *  `jsonb` — surfacing those is confusing). Pure. */
export function columnTypeLabel(
  c: { kind?: string | null; data_type?: string | null },
): string {
  switch (c.kind) {
    case "relation":
      return "relation";
    case "relation_many":
      return "relation · many";
    case "lookup":
      return "lookup";
    case "rollup":
      return "rollup";
    case "formula":
      return "formula";
    default:
      return c.data_type ?? "";
  }
}

/** Translate a draft into the create-column payload for its chosen kind. */
export function buildColumnPayload(d: ColumnDraft): ColumnIn {
  const trimmed = d.name.trim();
  if (d.dataType === "relation") {
    return {
      name: trimmed, data_type: "uuid", required: d.required, kind: "relation",
      config: { target_table: d.targetTable, on_delete: d.onDelete },
    };
  }
  if (d.dataType === "relation_many") {
    // Link-to-MANY: a jsonb array of ids; on-delete not yet supported (D20).
    return {
      name: trimmed, data_type: "jsonb", required: d.required, kind: "relation_many",
      config: { target_table: d.targetTable },
    };
  }
  if (d.dataType === "lookup") {
    return {
      name: trimmed, data_type: "text", kind: "lookup",
      config: { relation: d.lookupRelation, field: d.lookupField },
    };
  }
  if (d.dataType === "rollup") {
    const cfg: Record<string, unknown> = {
      source_table: d.rollupSource, via: d.rollupVia, op: d.rollupOp,
    };
    if (d.rollupOp !== "count") cfg.field = d.rollupField;
    const filters = d.rollupFilters.filter((f) => f.column.trim());
    if (filters.length) cfg.filters = filters;
    return {
      name: trimmed, data_type: d.rollupOp === "count" ? "integer" : "numeric",
      kind: "rollup", config: cfg,
    };
  }
  if (d.dataType === "formula") {
    // Per-row expression (e.g. `$json.qty * $json.price`); `data_type` is the
    // chosen result type, carried physical-less and coerced on read.
    return {
      name: trimmed, data_type: d.formulaResultType || "numeric",
      kind: "formula", config: { expression: d.formulaExpression },
    };
  }
  return {
    name: trimmed, data_type: d.dataType, required: d.required,
    // `select` (single) + `multiselect` (array) both carry the enum options.
    ...(d.dataType === "select" || d.dataType === "multiselect"
      ? { config: { options: d.options } }
      : {}),
  };
}
